import { useCallback, useEffect, useState } from 'react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { visibilityOptions } from '../data/widgets';
import {
  createTimelineEvent,
  deleteTimelineEvent,
  listOwnerTimelineEvents,
  updateTimelineEvent,
} from '../services/contentService';

const emptyEvent = {
  id: '',
  title: '',
  dateLabel: '',
  sortYear: '',
  description: '',
  visibility: 'private',
};

function Timeline() {
  const { currentUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(emptyEvent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadEvents = useCallback(async () => {
    if (!currentUser) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      setEvents(await listOwnerTimelineEvents(currentUser.uid));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  function updateForm(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function editEvent(event) {
    setForm({
      id: event.id,
      title: event.title || '',
      dateLabel: event.dateLabel || '',
      sortYear: event.sortYear || '',
      description: event.description || '',
      visibility: event.visibility || 'private',
    });
    setMessage('Editing timeline event.');
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      if (!form.title.trim()) {
        throw new Error('Timeline title is required.');
      }
      if (form.id) {
        await updateTimelineEvent(form.id, form);
        setMessage('Timeline event updated.');
      } else {
        await createTimelineEvent(currentUser.uid, form);
        setMessage('Timeline event created.');
      }
      setForm(emptyEvent);
      await loadEvents();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(eventId) {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await deleteTimelineEvent(eventId);
      setMessage('Timeline event deleted.');
      if (form.id === eventId) {
        setForm(emptyEvent);
      }
      await loadEvents();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <SEO title="Timeline" />
      <section className="page-heading">
        <p className="eyebrow">Chronology</p>
        <h1>Build a timeline of moments, seasons, and turning points.</h1>
        <p>Timeline events are privacy-aware and can stay private until they belong on the public page.</p>
      </section>
      {error && <p className="error-text">{error}</p>}
      {message && <p className="notice">{message}</p>}
      <section className="editor-layout">
        <form className="surface-card stacked-form" onSubmit={handleSubmit}>
          <h2>{form.id ? 'Edit event' : 'New event'}</h2>
          <label>Title<input value={form.title} onChange={(event) => updateForm('title', event.target.value)} /></label>
          <label>Date label<input value={form.dateLabel} onChange={(event) => updateForm('dateLabel', event.target.value)} placeholder="Summer 2008, Age 12, Chapter three" /></label>
          <label>Sort year<input type="number" value={form.sortYear} onChange={(event) => updateForm('sortYear', event.target.value)} placeholder="2008" /></label>
          <label>Description<textarea rows="7" value={form.description} onChange={(event) => updateForm('description', event.target.value)} /></label>
          <label>Visibility
            <select value={form.visibility} onChange={(event) => updateForm('visibility', event.target.value)}>
              {visibilityOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
            </select>
          </label>
          <div className="button-row compact-row">
            <button className="button" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save event'}</button>
            {form.id && <button className="button button-secondary" type="button" onClick={() => setForm(emptyEvent)}>Cancel edit</button>}
          </div>
        </form>
        <div className="surface-card">
          <h2>Your timeline</h2>
          {loading ? <p>Loading timeline...</p> : null}
          {!loading && events.length === 0 ? <p className="muted">No events yet. Add one turning point.</p> : null}
          <div className="item-list timeline-list">
            {events.map((event) => (
              <article className="content-item timeline-item" key={event.id}>
                <p className="tag">{event.visibility}</p>
                <h3>{event.title}</h3>
                <p className="muted">{event.dateLabel || event.sortYear || 'Undated'}</p>
                <p>{event.description || 'No description yet.'}</p>
                <div className="button-row compact-row">
                  <button className="button button-secondary" type="button" onClick={() => editEvent(event)}>Edit</button>
                  <button className="button button-secondary" type="button" onClick={() => handleDelete(event.id)} disabled={saving}>Delete</button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Timeline;
